<template>
  <AnalyticsWidget
    title="New Orders Breakdown"
    subTitle="New orders created during the date ranges"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">

    <LoadingPanel :loading="loading" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th></th>
          <th>Orders</th>
          <th>Order Count</th>
          <th>Panel Count</th>
          <th>Panel Area</th>
          <th>Stock Sheet Count</th>
          <th>Stock Sheet Area</th>
          <th><Tooltip label="Average sales price per panel/stock sheet" helpStyle>Avg Sales Price</Tooltip></th>
          <th><Tooltip label="Average cost price per panel/stock sheet" helpStyle>Avg Cost Price</Tooltip></th>
        </tr>
      </template>
      <template v-slot:body>
        <tr class="highlight">
          <td>
            Today
            <InlineHelpPopover title="Today" placement="right" inline>
              <p>Today&apos;s orders compared to previous working day&apos;s orders on {{formatDate(orderBreakdownData.day.previous.startDate)}}</p>              
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.day.current.orderValue" :previous="orderBreakdownData.day.previous.orderValue" highlight>{{formatCurrency(orderBreakdownData.day.current.orderValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.day.current.orderCount" :previous="orderBreakdownData.day.previous.orderCount">{{orderBreakdownData.day.current.orderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.day.current.panelCount" :previous="orderBreakdownData.day.previous.panelCount">{{orderBreakdownData.day.current.panelCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.day.current.panelArea" :previous="orderBreakdownData.day.previous.panelArea">{{formatDecimal(orderBreakdownData.day.current.panelArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.day.current.stockSheetCount" :previous="orderBreakdownData.day.previous.stockSheetCount">{{orderBreakdownData.day.current.stockSheetCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.day.current.stockSheetArea" :previous="orderBreakdownData.day.previous.stockSheetArea">{{formatDecimal(orderBreakdownData.day.current.stockSheetArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelSalePrice(orderBreakdownData.day.current)" :previous="calculateAveragePanelSalePrice(orderBreakdownData.day.previous)">{{formatCurrency(calculateAveragePanelSalePrice(orderBreakdownData.day.current))}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelCostPrice(orderBreakdownData.day.current)" :previous="calculateAveragePanelCostPrice(orderBreakdownData.day.previous)">{{formatCurrency(calculateAveragePanelCostPrice(orderBreakdownData.day.current))}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Week
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This week&apos;s orders compared to same day of the previous week&apos;s orders from {{formatDate(orderBreakdownData.week.previous.startDate)}} to {{formatDate(orderBreakdownData.week.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.week.current.orderValue" :previous="orderBreakdownData.week.previous.orderValue">{{formatCurrency(orderBreakdownData.week.current.orderValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.week.current.orderCount" :previous="orderBreakdownData.week.previous.orderCount">{{orderBreakdownData.week.current.orderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.week.current.panelCount" :previous="orderBreakdownData.week.previous.panelCount">{{orderBreakdownData.week.current.panelCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.week.current.panelArea" :previous="orderBreakdownData.week.previous.panelArea">{{formatDecimal(orderBreakdownData.week.current.panelArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.week.current.stockSheetCount" :previous="orderBreakdownData.week.previous.stockSheetCount">{{orderBreakdownData.week.current.stockSheetCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.week.current.stockSheetArea" :previous="orderBreakdownData.week.previous.stockSheetArea">{{formatDecimal(orderBreakdownData.week.current.stockSheetArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelSalePrice(orderBreakdownData.week.current)" :previous="calculateAveragePanelSalePrice(orderBreakdownData.week.previous)">{{formatCurrency(calculateAveragePanelSalePrice(orderBreakdownData.week.current))}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelCostPrice(orderBreakdownData.week.current)" :previous="calculateAveragePanelCostPrice(orderBreakdownData.week.previous)">{{formatCurrency(calculateAveragePanelCostPrice(orderBreakdownData.week.current))}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Month
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This month&apos;s orders compared to same day of the previous month&apos;s orders from {{formatDate(orderBreakdownData.month.previous.startDate)}} to {{formatDate(orderBreakdownData.month.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.month.current.orderValue" :previous="orderBreakdownData.month.previous.orderValue">{{formatCurrency(orderBreakdownData.month.current.orderValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.month.current.orderCount" :previous="orderBreakdownData.month.previous.orderCount">{{orderBreakdownData.month.current.orderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.month.current.panelCount" :previous="orderBreakdownData.month.previous.panelCount">{{orderBreakdownData.month.current.panelCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.month.current.panelArea" :previous="orderBreakdownData.month.previous.panelArea">{{formatDecimal(orderBreakdownData.month.current.panelArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.month.current.stockSheetCount" :previous="orderBreakdownData.month.previous.stockSheetCount">{{orderBreakdownData.month.current.stockSheetCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.month.current.stockSheetArea" :previous="orderBreakdownData.month.previous.stockSheetArea">{{formatDecimal(orderBreakdownData.month.current.stockSheetArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelSalePrice(orderBreakdownData.month.current)" :previous="calculateAveragePanelSalePrice(orderBreakdownData.month.previous)">{{formatCurrency(calculateAveragePanelSalePrice(orderBreakdownData.month.current))}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelCostPrice(orderBreakdownData.month.current)" :previous="calculateAveragePanelCostPrice(orderBreakdownData.month.previous)">{{formatCurrency(calculateAveragePanelCostPrice(orderBreakdownData.month.current))}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This FY
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This financial year&apos;s orders compared to same day of the previous financial year&apos;s orders from {{formatDate(orderBreakdownData.financialYear.previous.startDate)}} to {{formatDate(orderBreakdownData.financialYear.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.financialYear.current.orderValue" :previous="orderBreakdownData.financialYear.previous.orderValue">{{formatCurrency(orderBreakdownData.financialYear.current.orderValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.financialYear.current.orderCount" :previous="orderBreakdownData.financialYear.previous.orderCount">{{orderBreakdownData.financialYear.current.orderCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.financialYear.current.panelCount" :previous="orderBreakdownData.financialYear.previous.panelCount">{{orderBreakdownData.financialYear.current.panelCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.financialYear.current.panelArea" :previous="orderBreakdownData.financialYear.previous.panelArea">{{formatDecimal(orderBreakdownData.financialYear.current.panelArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.financialYear.current.stockSheetCount" :previous="orderBreakdownData.financialYear.previous.stockSheetCount">{{orderBreakdownData.financialYear.current.stockSheetCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderBreakdownData.financialYear.current.stockSheetArea" :previous="orderBreakdownData.financialYear.previous.stockSheetArea">{{formatDecimal(orderBreakdownData.financialYear.current.stockSheetArea)}}m&sup2;</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelSalePrice(orderBreakdownData.financialYear.current)" :previous="calculateAveragePanelSalePrice(orderBreakdownData.financialYear.previous)">{{formatCurrency(calculateAveragePanelSalePrice(orderBreakdownData.financialYear.current))}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="calculateAveragePanelCostPrice(orderBreakdownData.financialYear.current)" :previous="calculateAveragePanelCostPrice(orderBreakdownData.financialYear.previous)">{{formatCurrency(calculateAveragePanelCostPrice(orderBreakdownData.financialYear.current))}}</AnalyticsBreakdownValueCell>
        </tr>
      </template>

    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

export default {
  name: 'OrderBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell
  },
  props: {
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
  },
  data() {
    return {
      loading: true,
      orderBreakdownData: {}
    }
  },
  watch: {
    contactId: function() {
      this.requestData()
    },
    filter: function() {
      this.requestData()
    }
  },
  mounted() {
    this.loading = true
    this.requestData()

    this.subscribeEndpoint('/orders', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestData() {
      var params = {
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }
      this.$apiService.analyticsService.getOrdersValueBreakdown(params)
      .then(response => {
        this.orderBreakdownData = response.data
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
      })
    },
    calculateAveragePanelSalePrice(breakdownInfo) {
      var currentTotalItems = breakdownInfo.panelCount + breakdownInfo.stockSheetCount
      if(currentTotalItems > 0)
        return breakdownInfo.orderValue / currentTotalItems
      
      return 0
    },
    calculateAveragePanelCostPrice(breakdownInfo) {
      var currentTotalItems = breakdownInfo.panelCount + breakdownInfo.stockSheetCount
      if(currentTotalItems > 0)
        return (breakdownInfo.panelCOG + breakdownInfo.sheetCOG) / currentTotalItems

      return 0
    },
  }
}
</script>